import { useEffect, useState, useCallback } from 'react'
import { MessageSquare, Users, TrendingUp, CheckCircle, ArrowUpRight, AlertTriangle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from '@/i18n'
import { API_BASE_URL } from '@/config'
import { auth } from '@/firebase'

interface DashboardStats {
  totalSessions: number
  activeSessions: number
  totalMessages: number
  avgResponseTime: number
  satisfactionScore: number
}

interface UsageInfo {
  currentUsage: number
  limit: number
  plan: string
}

interface SessionItem {
  id: string
  userId?: string
  userInfo?: { ip?: string; userAgent?: string }
  status: 'active' | 'ended'
  messageCount: number
  startedAt: string
}

interface TopicItem {
  topic: string
  count: number
  trend?: string
}

export default function Dashboard() {
  const { user, tenant } = useAuth()
  const { t } = useTranslation()
  const [stats, setStats] = useState<DashboardStats>({
    totalSessions: 0,
    activeSessions: 0,
    totalMessages: 0,
    avgResponseTime: 0,
    satisfactionScore: 0
  })
  const [usage, setUsage] = useState<UsageInfo | null>(null)
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [topics, setTopics] = useState<TopicItem[]>([])
  const [loading, setLoading] = useState(true)

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true)
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
      const tenantId = user?.tenantId

      // Fetch projects list
      let projectId: string | null = null
      try {
        const projectsRes = await fetch(`${API_BASE_URL}/projects`, { headers })
        if (projectsRes.ok) {
          const projectsData = await projectsRes.json()
          const projects = projectsData.projects || []
          if (projects.length > 0) {
            projectId = projects[0].id
          }
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err)
      }

      // Fetch analytics if we have a project
      if (projectId) {
        try {
          const analyticsRes = await fetch(`${API_BASE_URL}/analytics/${projectId}`, { headers })
          if (analyticsRes.ok) {
            const analyticsData = await analyticsRes.json()
            setStats({
              totalSessions: analyticsData.totalSessions || 0,
              activeSessions: 0,
              totalMessages: analyticsData.totalMessages || 0,
              avgResponseTime: analyticsData.avgResponseTime || 0,
              satisfactionScore: analyticsData.satisfactionScore || 0
            })
          }
        } catch (err) {
          console.error('Failed to fetch analytics:', err)
        }

        // Fetch recent sessions
        try {
          const sessionsRes = await fetch(`${API_BASE_URL}/projects/${projectId}/sessions?limit=5`, { headers })
          if (sessionsRes.ok) {
            const sessionsData = await sessionsRes.json()
            setSessions(sessionsData.sessions || [])
          }
        } catch (err) {
          console.error('Failed to fetch sessions:', err)
        }

        // Fetch topics
        try {
          const topicsRes = await fetch(`${API_BASE_URL}/analytics/${projectId}/topics?limit=5`, { headers })
          if (topicsRes.ok) {
            const topicsData = await topicsRes.json()
            setTopics(topicsData.topics || [])
          }
        } catch (err) {
          console.error('Failed to fetch topics:', err)
        }
      }

      // Fetch billing/subscription for usage info
      if (tenantId) {
        try {
          const billingRes = await fetch(`${API_BASE_URL}/billing/subscription`, {
            headers: { ...headers, 'X-Tenant-Id': tenantId }
          })
          if (billingRes.ok) {
            const billingData = await billingRes.json()
            const sub = billingData.subscription
            if (sub) {
              setUsage({
                currentUsage: sub.conversationsUsed ?? 0,
                limit: sub.conversationsLimit ?? 50,
                plan: sub.plan || tenant?.plan || 'free'
              })
            }
          }
        } catch (err) {
          console.error('Failed to fetch subscription:', err)
        }
      }

      // Fallback usage from tenant if billing endpoint failed
      if (!usage && tenant) {
        const plan = tenant.plan || 'free'
        const PLAN_LIMITS: Record<string, number> = {
          free: 50, starter: 500, pro: 3000, business: 10000, enterprise: Infinity
        }
        setUsage({
          currentUsage: 0,
          limit: PLAN_LIMITS[plan] ?? PLAN_LIMITS.free,
          plan
        })
      }
    } catch (err) {
      console.error('Dashboard data fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [user, tenant])

  useEffect(() => {
    if (user) {
      fetchDashboardData()
    }
  }, [user, fetchDashboardData])

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${(seconds / 3600).toFixed(1)}h`
  }

  const formatTimeAgo = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return t('dashboard.justNow') || 'just now'
    if (minutes < 60) return `${minutes} ${t('dashboard.minAgo')}`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  const statCards = [
    {
      title: t('dashboard.todayConversations'),
      value: stats.totalSessions.toLocaleString(),
      icon: MessageSquare,
      change: '',
      changeColor: 'text-green-600'
    },
    {
      title: t('dashboard.activeUsers'),
      value: stats.totalMessages.toLocaleString(),
      icon: Users,
      change: '',
      changeColor: 'text-green-600'
    },
    {
      title: t('dashboard.resolutionRate'),
      value: stats.satisfactionScore > 0 ? `${stats.satisfactionScore}` : '-',
      icon: CheckCircle,
      change: '',
      changeColor: 'text-green-600'
    },
    {
      title: t('dashboard.avgResponseTime'),
      value: stats.avgResponseTime > 0 ? formatDuration(stats.avgResponseTime) : '-',
      icon: TrendingUp,
      change: '',
      changeColor: 'text-green-600'
    }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('dashboard.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.title}
            className="relative overflow-hidden rounded-lg bg-white px-6 py-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            {loading ? (
              <div className="animate-pulse">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  <div className="ml-4 h-4 bg-gray-200 rounded w-20"></div>
                </div>
                <div className="mt-3 h-8 bg-gray-200 rounded w-16"></div>
                <div className="mt-2 h-3 bg-gray-200 rounded w-12"></div>
              </div>
            ) : (
              <>
                <div className="flex items-center">
                  <stat.icon className="h-8 w-8 text-indigo-600" />
                  <h3 className="ml-4 text-sm font-medium text-gray-900">{stat.title}</h3>
                </div>
                <p className="mt-3 text-2xl font-semibold text-gray-900">{stat.value}</p>
                {stat.change && (
                  <div className="mt-2 flex items-center text-sm">
                    <span className={`font-medium ${stat.changeColor}`}>{stat.change}</span>
                    <span className="ml-1 text-gray-500">{t('dashboard.vsLastWeek')}</span>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {usage && usage.limit !== Infinity && (
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.monthlyConversations')}</h3>
            <span className="text-sm text-gray-500 capitalize">{usage.plan} {t('dashboard.plan')}</span>
          </div>
          <div className="flex items-end justify-between mb-2">
            <span className="text-2xl font-semibold text-gray-900">
              {usage.currentUsage.toLocaleString()} <span className="text-sm font-normal text-gray-500">/ {usage.limit.toLocaleString()}</span>
            </span>
            <span className="text-sm text-gray-500">
              {Math.round((usage.currentUsage / usage.limit) * 100)}% {t('dashboard.used')}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
            <div
              className={`h-3 rounded-full transition-all ${
                usage.currentUsage / usage.limit > 0.8
                  ? 'bg-red-500'
                  : usage.currentUsage / usage.limit > 0.6
                  ? 'bg-yellow-500'
                  : 'bg-indigo-600'
              }`}
              style={{ width: `${Math.min((usage.currentUsage / usage.limit) * 100, 100)}%` }}
            ></div>
          </div>
          {usage.currentUsage / usage.limit > 0.8 && usage.currentUsage < usage.limit && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 border border-yellow-200">
              <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
              <p className="text-sm text-yellow-800">{t('dashboard.approachingLimit')}</p>
            </div>
          )}
          {usage.currentUsage >= usage.limit && (
            <div className="flex items-center justify-between p-3 rounded-md bg-red-50 border border-red-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{t('dashboard.limitReached')}</p>
              </div>
              <a
                href="/settings"
                className="ml-4 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
              >
                {t('dashboard.upgradePlan')}
              </a>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.recentConversations')}</h3>
            <button className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center">
              {t('common.viewAll')}
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                    <div>
                      <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                  <div className="h-5 bg-gray-200 rounded w-16"></div>
                </div>
              ))
            ) : sessions.length > 0 ? (
              sessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-indigo-600 text-sm font-medium">
                        {(session.userId || 'A').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {session.userId || session.userInfo?.ip || `Session ${session.id.slice(0, 8)}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {session.messageCount} msgs - {formatTimeAgo(session.startedAt)}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    session.status === 'active'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {session.status === 'active' ? t('dashboard.active') || 'active' : t('dashboard.resolved')}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-sm text-gray-500">
                {t('dashboard.noConversations') || 'No recent conversations'}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.topTopics')}</h3>
            <button className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center">
              {t('common.viewAll')}
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </button>
          </div>
          <div className="space-y-4">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center justify-between mb-1">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2"></div>
                </div>
              ))
            ) : topics.length > 0 ? (
              topics.map((item) => {
                const maxCount = topics[0]?.count || 1
                const percentage = Math.round((item.count / maxCount) * 100)
                return (
                  <div key={item.topic}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{item.topic}</span>
                      <span className="text-sm text-gray-500">{item.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-center py-6 text-sm text-gray-500">
                {t('dashboard.noTopics') || 'No topic data available'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
